import { Album } from '../types/album';
import { musicbrainzService } from './musicbrainzService';
import { secureSupabaseService } from './secureSupabaseService';
import { generateSortKeyWithMusicBrainz } from '../utils/sorting';

export interface ArtistEnhancementProgress {
  stage: 'starting' | 'lookingUp' | 'updating' | 'complete' | 'error';
  processed: number;
  total: number;
  currentArtist: string;
  errors: string[];
  enhanced: number;
}

export interface ArtistEnhancementResult {
  success: boolean;
  processed: number;
  enhanced: number;
  errors: string[];
  duration: number;
}

class ArtistEnhancementService {
  private isRunning = false;
  private currentProgress: ArtistEnhancementProgress | null = null;

  async enhanceAlbumSorting(
    albums: Album[],
    onProgress?: (progress: ArtistEnhancementProgress) => void
  ): Promise<ArtistEnhancementResult> {
    if (this.isRunning) {
      throw new Error('Artist enhancement is already running');
    }

    this.isRunning = true;
    const startTime = Date.now();

    try {
      // Get unique artist names from both primary artist and collaborations
      const allArtistNames = albums.flatMap(album => album.artists || [album.artist]);
      const uniqueArtists = [...new Set(allArtistNames)];
      
      this.currentProgress = {
        stage: 'starting',
        processed: 0,
        total: uniqueArtists.length,
        currentArtist: '',
        errors: [],
        enhanced: 0
      };

      onProgress?.(this.currentProgress);

      // Batch lookup artist information
      this.currentProgress.stage = 'lookingUp';
      onProgress?.(this.currentProgress);

      const artistLookupResults = await musicbrainzService.batchLookupArtistTypes(
        uniqueArtists,
        (processed, total, currentArtist) => {
          console.log(`Enhancement service progress callback: ${processed}/${total} - ${currentArtist}`);
          // Create a new object to trigger React state updates
          this.currentProgress = {
            ...this.currentProgress!,
            processed,
            total,
            currentArtist
          };
          console.log('Calling onProgress with:', this.currentProgress);
          onProgress?.(this.currentProgress);
        }
      );

      // Update albums with improved sort keys
      this.currentProgress.stage = 'updating';
      this.currentProgress.processed = 0;
      this.currentProgress.total = albums.length;
      this.currentProgress.currentArtist = '';
      onProgress?.(this.currentProgress);

      const enhancedAlbums: Album[] = [];
      let enhancementCount = 0;

      for (let i = 0; i < albums.length; i++) {
        const album = albums[i];
        const lookupResult = artistLookupResults.get(album.artist);

        // Create new progress object for React state updates
        this.currentProgress = {
          ...this.currentProgress,
          currentArtist: `${album.artist} - ${album.title}`,
          processed: i + 1
        };

        try {
          // Generate new sort key with MusicBrainz data
          const newSortKey = generateSortKeyWithMusicBrainz(album, lookupResult);
          
          // Check if any enhancement is needed (sort key, country, or language)
          const needsEnhancement = newSortKey !== album.sortKey ||
            (lookupResult?.country && !album.artistCountry) ||
            (lookupResult?.countryName && !album.artistCountryName);
          
          if (needsEnhancement) {
            const enhancedAlbum = {
              ...album,
              sortKey: newSortKey,
              // Add country information from MusicBrainz if available
              ...(lookupResult?.country && { artistCountry: lookupResult.country }),
              ...(lookupResult?.countryName && { artistCountryName: lookupResult.countryName })
            };
            
            // Save to IndexedDB
            await secureSupabaseService.saveAlbum(enhancedAlbum);
            enhancedAlbums.push(enhancedAlbum);
            enhancementCount++;
            
            this.currentProgress = {
              ...this.currentProgress,
              enhanced: enhancementCount
            };
          } else {
            enhancedAlbums.push(album);
          }

        } catch (error) {
          console.error(`Failed to enhance album "${album.title}" by ${album.artist}:`, error);
          this.currentProgress.errors.push(`${album.artist} - ${album.title}: ${error}`);
          enhancedAlbums.push(album); // Keep original album
        }

        // Report progress every 10 albums
        if (i % 10 === 0 || i === albums.length - 1) {
          onProgress?.(this.currentProgress);
        }
      }

      this.currentProgress.stage = 'complete';
      onProgress?.(this.currentProgress);

      const duration = Date.now() - startTime;

      return {
        success: true,
        processed: albums.length,
        enhanced: enhancementCount,
        errors: this.currentProgress.errors,
        duration
      };

    } catch (error) {
      console.error('Artist enhancement failed:', error);
      
      if (this.currentProgress) {
        this.currentProgress.stage = 'error';
        this.currentProgress.errors.push(`Enhancement failed: ${error}`);
        onProgress?.(this.currentProgress);
      }

      const duration = Date.now() - startTime;

      return {
        success: false,
        processed: this.currentProgress?.processed || 0,
        enhanced: this.currentProgress?.enhanced || 0,
        errors: this.currentProgress?.errors || [String(error)],
        duration
      };

    } finally {
      this.isRunning = false;
      this.currentProgress = null;
    }
  }

  // Quick single-album enhancement for immediate feedback
  async enhanceSingleAlbum(album: Album): Promise<Album> {
    try {
      const lookupResult = await musicbrainzService.lookupArtistType(album.artist);
      const newSortKey = generateSortKeyWithMusicBrainz(album, lookupResult);
      
      if (newSortKey !== album.sortKey) {
        const enhancedAlbum = {
          ...album,
          sortKey: newSortKey
        };
        
        await secureSupabaseService.saveAlbum(enhancedAlbum);
        return enhancedAlbum;
      }
      
      return album;
      
    } catch (error) {
      console.warn(`Single album enhancement failed for ${album.artist}:`, error);
      return album;
    }
  }

  isEnhancementRunning(): boolean {
    return this.isRunning;
  }

  getCurrentProgress(): ArtistEnhancementProgress | null {
    return this.currentProgress;
  }

  // Get statistics about MusicBrainz cache
  getCacheStats() {
    return musicbrainzService.getCacheStats();
  }

  // Clear MusicBrainz cache (useful for testing)
  clearCache(): void {
    musicbrainzService.clearCache();
  }
}

export const artistEnhancementService = new ArtistEnhancementService();